import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Refresh user data function - can be called from anywhere
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Auto-refresh user data every 30 seconds to catch permission changes
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      refreshUser();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [token, refreshUser]);

  // Refresh on window focus (when user switches back to tab)
  useEffect(() => {
    if (!token) return;
    
    const handleFocus = () => {
      refreshUser();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token, refreshUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem("token", access_token);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Login failed"
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    
    // Extract module from permission (e.g., "leads:write" -> "leads")
    const module = permission.split(":")[0];
    
    // HR Manager has full access to team/hr related modules and settings
    if (user.role === "HR Manager") {
      const hrAllowedModules = ["team", "hr", "dashboard", "settings"];
      if (hrAllowedModules.includes(module) || permission === "settings:read") {
        return true;
      }
    }
    
    // Check if user has this module in their assigned_modules
    return user.assigned_modules?.includes(module) || false;
  };

  const hasModuleAccess = (moduleName) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    
    // HR Manager has access to team, hr, dashboard, and settings modules
    if (user.role === "HR Manager") {
      const hrAllowedModules = ["team", "hr", "dashboard", "settings"];
      if (hrAllowedModules.includes(moduleName)) return true;
    }
    
    return user.assigned_modules?.includes(moduleName) || false;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, hasPermission, hasModuleAccess, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
